import { useCallback, useEffect } from 'react';
import { authStoreUserTournamentId } from 'modules/auth/store/selectors';
import {
  clearChatHistory,
  loadChatHistory,
  selectChatError,
  selectChatIsClearing,
  selectChatIsSending,
  selectChatMessages,
  selectChatPendingConfirmation,
  selectChatQuota,
  sendChatMessage,
} from 'modules/chat/store';
import { useAppDispatch, useAppSelector } from 'store/hooks';

interface UseChatOptions {
  enabled?: boolean;
}

export function useChat(options?: UseChatOptions) {
  const enabled = options?.enabled ?? true;
  const dispatch = useAppDispatch();

  const messages = useAppSelector(selectChatMessages);
  const isSending = useAppSelector(selectChatIsSending);
  const isClearing = useAppSelector(selectChatIsClearing);
  const error = useAppSelector(selectChatError);
  const quota = useAppSelector(selectChatQuota);
  const pendingConfirmation = useAppSelector(selectChatPendingConfirmation);
  const activeTournamentId = useAppSelector(authStoreUserTournamentId);

  useEffect(() => {
    if (!enabled || !activeTournamentId) {
      return;
    }

    dispatch(loadChatHistory());
  }, [dispatch, enabled, activeTournamentId]);

  const sendMessage = useCallback(
    async (message: string) => {
      await dispatch(sendChatMessage(message));
    },
    [dispatch],
  );

  const clearHistory = useCallback(async () => {
    await dispatch(clearChatHistory());
  }, [dispatch]);

  const confirmWrite = useCallback(async () => {
    // eslint-disable-next-line lingui/no-unlocalized-strings -- слово подтверждения для бэкенда ИИ-ассистента, не подпись интерфейса
    await dispatch(sendChatMessage('да'));
  }, [dispatch]);

  const cancelWrite = useCallback(async () => {
    // eslint-disable-next-line lingui/no-unlocalized-strings -- слово отказа для бэкенда ИИ-ассистента, не подпись интерфейса
    await dispatch(sendChatMessage('нет'));
  }, [dispatch]);

  return {
    messages,
    isSending,
    isClearing,
    error,
    quota,
    pendingConfirmation,
    sendMessage,
    clearHistory,
    confirmWrite,
    cancelWrite,
  };
}
